import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { Icon } from '../../Icon';
import { MemoryModal } from '../MemoryModal';
import { MemoryChatComposer } from './MemoryChatComposer';
import { useChatView } from '../../../shared/use-chat-view';
import { MEMORY_KEEPER_TOUR_TARGETS } from '../../../../lib/shared/memory-keeper-tour-steps';
import { logEvent } from '../../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../../lib/shared/event-types';
import styles from './MemoryChatThread.css';

const cx = classnames.bind(styles);

function MemoryChatThread({ activeChapter, messages, isAssistantTyping, isChatFinished, sendErrorMessage, onSendMessage }) {
    const messagesRef = useRef(null);
    const { isChatView } = useChatView();
    const [isMemoryOpen, setIsMemoryOpen] = useState(false);
    const [dismissedChapterId, setDismissedChapterId] = useState(null);
    const isChapterReady = activeChapter.isMemoryUnlocked && dismissedChapterId !== activeChapter.id;
    const isMemoryPending = activeChapter.isMemoryPending;

    useEffect(() => {
        const list = messagesRef.current;

        if (list) {
            list.scrollTop = list.scrollHeight;
        }
    }, [messages, isAssistantTyping, isChatView]);

    useEffect(() => {
        if (isChatFinished) {
            logEvent(EVENT_TYPES.MEMORY_KEEPER_CHAT_FINISHED_VIEWED, { 'chapter_id': activeChapter.id, 'answer_count': activeChapter.answerCount });
        }
    }, [isChatFinished, activeChapter.id]);

    useEffect(() => {
        if (isChapterReady) {
            logEvent(EVENT_TYPES.MEMORY_KEEPER_MEMORY_SAVED, { 'chapter_id': activeChapter.id });
        }
    }, [isChapterReady, activeChapter.id]);

    return (
        <div className={styles.thread}>
            <ul
                key={activeChapter.label}
                className={styles.messages}
                ref={messagesRef}
                aria-live='polite'
                data-tour={MEMORY_KEEPER_TOUR_TARGETS.THREAD}
            >
                {messages.map((message) => {
                    const isOwn = message.author === 'user';

                    return (
                        <li key={message.id} className={cx('messageRow', { own: isOwn })}>
                            {isOwn ? null : <span className={styles.author}>Boundly</span>}
                            <div className={cx('bubble', { ownBubble: isOwn })}>{message.text}</div>
                        </li>
                    );
                })}

                {isAssistantTyping && (
                    <li className={styles.messageRow}>
                        <span className={styles.author}>Boundly</span>
                        <div className={cx('bubble', 'typingBubble')}>
                            <span className={styles.typingLabel}>Boundly is writing a reply</span>
                            <span className={styles.typingDot} aria-hidden='true' />
                            <span className={styles.typingDot} aria-hidden='true' />
                            <span className={styles.typingDot} aria-hidden='true' />
                        </div>
                    </li>
                )}

            </ul>

            {isChapterReady && (
                <div className={styles.chapterReadyBar}>
                    <p className={styles.chapterReadyText}>
                        {isMemoryPending ? (
                            'Your memory is saved. We’re saving your chapter…'
                        ) : (
                            <>
                                Your memory is saved. You can{' '}
                                <button
                                    type='button'
                                    className={styles.chapterReadyLink}
                                    onClick={() => {
                                        logEvent(EVENT_TYPES.MEMORY_KEEPER_MEMORY_VIEWED, { 'chapter_id': activeChapter.id });
                                        setIsMemoryOpen(true);
                                    }}
                                >
                                    view it
                                </button>
                            </>
                        )}
                    </p>
                    <button
                        type='button'
                        className={styles.chapterReadyClose}
                        aria-label='Dismiss'
                        onClick={() => setDismissedChapterId(activeChapter.id)}
                    >
                        <Icon name='close' className={styles.chapterReadyCloseIcon} />
                    </button>
                </div>
            )}

            {isChatFinished ? (
                <p className={styles.chatFinishedNote}>
                    You&apos;ve answered every question in this chapter. There&apos;s nothing more to add here.
                </p>
            ) : (
                <MemoryChatComposer onSend={onSendMessage} sendErrorMessage={sendErrorMessage} />
            )}

            <MemoryModal
                isOpen={isMemoryOpen}
                onCloseModal={() => setIsMemoryOpen(false)}
                chapter={activeChapter.id}
            />
        </div>
    );
}

MemoryChatThread.propTypes = {
    activeChapter: PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        answerCount: PropTypes.number.isRequired,
        isMemoryUnlocked: PropTypes.bool.isRequired,
        isMemoryPending: PropTypes.bool.isRequired
    }).isRequired,
    messages: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        author: PropTypes.string.isRequired,
        text: PropTypes.string.isRequired
    })).isRequired,
    isAssistantTyping: PropTypes.bool.isRequired,
    isChatFinished: PropTypes.bool.isRequired,
    sendErrorMessage: PropTypes.string,
    onSendMessage: PropTypes.func.isRequired
};

MemoryChatThread.defaultProps = {
    sendErrorMessage: null
};

export {
    MemoryChatThread
};
